import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import Field from 'redux-form/es/Field';
import formValueSelector from 'redux-form/es/formValueSelector';
import actions from 'redux-form/es/actions';
import moment from 'moment';
import cx from 'classnames';
import InputSelectField from 'components/Credit24/Core/Controls/InputSelectField';

const { change } = actions;

const propTypes = {
  minDate: PropTypes.object.isRequired,
  maxDate: PropTypes.object.isRequired,
  change: PropTypes.func.isRequired,
  titleKey: PropTypes.string,
  fieldNameYear: PropTypes.string,
  fieldNameMonth: PropTypes.string,
  fieldNameDay: PropTypes.string,
  year: PropTypes.string,
  month: PropTypes.string,
  day: PropTypes.string,
  disabled: PropTypes.bool,
  isDayHidden: PropTypes.bool,
  isMonthNumeric: PropTypes.bool,
  order: PropTypes.string,
  formId: PropTypes.string,
};

const defaultProps = {
  fieldNameYear: 'datePickerYear',
  fieldNameMonth: 'datePickerMonth',
  fieldNameDay: 'datePickerDay',
  order: 'asc',
  formId: 'loan',
};

const fieldDefaultValue = 'empty';

const countNumberOfDays = (year, month) => {
  if (month && month !== fieldDefaultValue) {
    if (year && year !== fieldDefaultValue) {
      return moment(`${year}-${month}`, 'YYYY-M').daysInMonth();
    }
    return moment(`${month}`, 'M').daysInMonth();
  }
  return 31;
};

const generateDaysList = (year, month, isDisabled = () => false) => (
  [...Array(countNumberOfDays(year, month)).keys()].map((no) =>
    ({ type: no + 1, title: no + 1, disabled: isDisabled(no + 1) }))
);

const generateYearsList = (yearMin, yearMax, order) => (
  [...Array((yearMax - yearMin) + 1).keys()].map((no) => {
    const year = order === 'desc' ? yearMax - no : yearMin + no;
    return {
      type: year,
      title: year,
    };
  })
);

const generateMonthsList = (isMonthNumeric) => [...Array(12).keys()].map((no) => {
  const type = no + 1;
  return {
    title: isMonthNumeric ? type : `month.${type}`,
    type,
    intl: !isMonthNumeric,
  };
});

class DatePicker extends Component {
  constructor(props) {
    super(props);
    const {
      year, month, minDate, maxDate, order, isMonthNumeric,
    } = props;
    const correctMinDate = moment.min(minDate, maxDate);
    const correctMaxDate = moment.max(minDate, maxDate);
    this.state = {
      minDate: correctMinDate,
      maxDate: correctMaxDate,
      yearsList: generateYearsList(correctMinDate.year(), correctMaxDate.year(), order),
      monthsList: generateMonthsList(isMonthNumeric),
      daysList: generateDaysList(year, month),
    };
    this.isDateAvailableForYearMonthList = this.isDateAvailableForYearMonthList.bind(this);
    this.isDateAvailableForDaysList = this.isDateAvailableForDaysList.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { year, month, day } = nextProps;
    this.setState((prevState) => ({
      yearsList: prevState.yearsList.map((item) => ({
        ...item,
        disabled: !this.isDateAvailableForYearMonthList(month, item.type, month, day),
      })),
      monthsList: prevState.monthsList.map((item) => ({
        ...item,
        disabled: !this.isDateAvailableForYearMonthList(year, year, item.type, day),
      })),
      daysList: generateDaysList(year, month, (dayNumber) => (
        !this.isDateAvailableForDaysList(year, month, dayNumber)
      )),
    }), () => {
      if (day > countNumberOfDays(year, month)) {
        this.props.change(this.props.formId, this.props.fieldNameDay, fieldDefaultValue);
      }
    });
  }

  isDateAvailableForYearMonthList(selectedValue, year, month, day) {
    const { minDate, maxDate } = this.state;
    if (selectedValue && selectedValue !== fieldDefaultValue) {
      let date = null;
      let units = null;
      if (day && day !== fieldDefaultValue) {
        date = moment(`${year}-${month}-${Math.min(day, countNumberOfDays(year, month))}`, 'YYYY-M-D');
        units = 'day';
      } else {
        date = moment(`${year}-${month}`, 'YYYY-M');
        units = 'month';
      }
      return date.isBetween(minDate, maxDate, units, '[]');
    }
    return true;
  }

  isDateAvailableForDaysList(year, month, day) {
    const { minDate, maxDate } = this.state;
    if (year && year !== fieldDefaultValue && month && month !== fieldDefaultValue) {
      return moment(`${year}-${month}-${day}`, 'YYYY-M-D').isBetween(minDate, maxDate, 'day', '[]');
    }
    return true;
  }

  render() {
    const { daysList, monthsList, yearsList } = this.state;
    const {
      fieldNameDay, fieldNameMonth, fieldNameYear, disabled, isDayHidden,
    } = this.props;
    const fieldMonthClasses = cx({
      grid__item: true,
      'one-third': true,
      'palm-two-thirds': !isDayHidden,
      'palm-one-half': isDayHidden,
    });
    const fieldYearClasses = cx({
      grid__item: true,
      'one-third': true,
      'palm-one-whole': !isDayHidden,
      'palm-one-half': isDayHidden,
    });
    return (
      <div className="input-group">
        <div className="input-group__title">
          <FormattedMessage id={this.props.titleKey} />
        </div>
        <Field
          name="datePickerUnderage"
          component={
            ({ meta: { error } }) => {
              if (error) {
                return (
                  <div className="message message--error">
                    <span className="message__icon" />
                    <FormattedMessage id={error} />
                  </div>
                );
              }
              return null;
            }
          }
        />
        <div className="grid">
          {!isDayHidden
            && (
            <div className="grid__item one-third">
              <Field
                name={fieldNameDay}
                component={InputSelectField}
                options={daysList}
                emptyTextKey="registration.field.date.day"
                disabled={disabled}
                isFullWidth
              />
            </div>
            )}
          <div className={fieldMonthClasses}>
            <Field
              name={fieldNameMonth}
              component={InputSelectField}
              options={monthsList}
              emptyTextKey="registration.field.date.month"
              disabled={disabled}
              isFullWidth
            />
          </div>
          <div className={fieldYearClasses}>
            <Field
              name={fieldNameYear}
              component={InputSelectField}
              options={yearsList}
              emptyTextKey="registration.field.date.year"
              disabled={disabled}
              isFullWidth
            />
          </div>
        </div>
      </div>
    );
  }
}

DatePicker.propTypes = propTypes;
DatePicker.defaultProps = defaultProps;

export default connect(
  (state, props) => {
    const selector = formValueSelector(props.formId || defaultProps.formId);
    return {
      year: selector(state, props.fieldNameYear || defaultProps.fieldNameYear),
      month: selector(state, props.fieldNameMonth || defaultProps.fieldNameMonth),
      day: selector(state, props.fieldNameDay || defaultProps.fieldNameDay),
    };
  },
  { change }
)(DatePicker);
